import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Loader2, Send } from "lucide-react";
import { SAMPLE_REVIEWS } from "../lib/site";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ value, size = 16, onChange = null, testIdPrefix = "star" }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((i) => {
        const active = i <= value;
        const interactive = !!onChange;
        return (
          <button
            type="button"
            key={i}
            disabled={!interactive}
            onClick={() => interactive && onChange(i)}
            data-testid={`${testIdPrefix}-${i}`}
            aria-label={`${i} star`}
            className={`${interactive ? "cursor-pointer hover:scale-110" : "cursor-default"} transition`}
          >
            <Star
              size={size}
              fill={active ? "#EAB308" : "transparent"}
              className={active ? "text-yellow-500" : "text-slate-300"}
              strokeWidth={2}
            />
          </button>
        );
      })}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", rating: 5, message: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reviews`);
      const merged = [...data];
      // If less than 4 backend reviews, top up display with seed reviews (not stored)
      if (merged.length < 4) {
        SAMPLE_REVIEWS.forEach((s) => {
          if (merged.length < 6) merged.push(s);
        });
      }
      setReviews(merged);
    } catch (e) {
      setReviews(SAMPLE_REVIEWS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error("Apna naam likhiye (min 2 characters)");
      return;
    }
    if (!form.message.trim() || form.message.trim().length < 5) {
      toast.error("Apna review likhiye (min 5 characters)");
      return;
    }
    if (form.rating < 1 || form.rating > 5) {
      toast.error("Rating select kijiye");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        name: form.name.trim(),
        location: form.location.trim() || null,
        rating: form.rating,
        message: form.message.trim(),
      });
      toast.success("Shukriya! Aapka review submit ho gaya.");
      setForm({ name: "", location: "", rating: 5, message: "" });
      await load();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Bahut zyada requests. Thodi der baad try kijiye.");
      } else {
        toast.error("Review submit nahi ho saka. Thodi der baad try kijiye.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const avg =
    reviews.length > 0
      ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)
      : "5.0";

  return (
    <section id="reviews" data-testid="reviews-section" className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-block px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold uppercase tracking-wider">
            Customer Love
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Reviews &amp; <span className="text-yellow-600">Ratings</span>
          </h2>
          <div className="mt-4 flex items-center justify-center gap-3">
            <Stars value={Math.round(avg)} size={22} />
            <div className="text-2xl font-extrabold text-slate-900" data-testid="reviews-avg">{avg}</div>
            <div className="text-sm text-slate-500">/ 5 · {reviews.length}+ reviews</div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Review list */}
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5">
            {loading ? (
              <div className="col-span-full flex items-center justify-center py-16">
                <Loader2 className="animate-spin text-blue-700" />
              </div>
            ) : (
              reviews.slice(0, 6).map((r, idx) => (
                <article
                  key={r.id || idx}
                  data-testid={`review-card-${idx}`}
                  className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg hover:-translate-y-0.5 transition"
                >
                  <div className="flex items-center justify-between mb-3">
                    <Stars value={r.rating} />
                    <span className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">Verified</span>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">"{r.message}"</p>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-700 to-orange-500 text-white grid place-items-center font-bold">
                      {r.name?.[0]?.toUpperCase() || "C"}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-slate-900">{r.name}</div>
                      <div className="text-xs text-slate-500">{r.location || "Delhi NCR"}</div>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>

          {/* Review form */}
          <form
            onSubmit={submit}
            data-testid="review-form"
            className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm h-fit sticky top-24"
          >
            <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>
              Apna Review Dijiye
            </h3>
            <p className="text-sm text-slate-500 mb-5">Aapka feedback humare liye important hai.</p>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">Naam *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="review-name-input"
                  required
                  className="mt-1 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-200 outline-none text-sm"
                  placeholder="Aapka naam"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Location</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  data-testid="review-location-input"
                  className="mt-1 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-200 outline-none text-sm"
                  placeholder="Delhi / Noida / Gurgaon"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Rating *</label>
                <Stars
                  value={form.rating}
                  size={28}
                  onChange={(v) => setForm({ ...form, rating: v })}
                  testIdPrefix="review-rating-star"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Review *</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="review-message-input"
                  required
                  className="mt-1 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-200 outline-none text-sm resize-none"
                  placeholder="Apna experience share kijiye..."
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="review-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold shadow-md hover:shadow-xl transition"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
                {submitting ? "Submitting..." : "Submit Review"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
